<div class="complaint-review-container">
  <!-- Top Navigation Bar -->
  <header class="review-header">
    <div class="header-left">
      <button class="btn-back" (click)="goBack()" title="Back">
        <mat-icon>arrow_back</mat-icon>
      </button>
      <h1 class="review-title" [title]="headerTitle">{{ headerTitle }}</h1>
    </div>
    <div class="header-right" *ngIf="!isReadOnly">
      <button class="btn-reject" (click)="onReject()" [disabled]="isSubmitting || isRejecting">Reject</button>
      <button class="btn-approve" (click)="onApprove()" [disabled]="isSubmitting || isRejecting">
        {{ isSubmitting ? 'Submitting...' : 'Approve' }}
      </button>
    </div>
  </header>

  <!-- Loading State -->
  <div *ngIf="isLoading" class="loading-container">
    <div class="loading-spinner"></div>
    <p>Loading complaint data...</p>
  </div>

  <!-- Error State -->
  <div *ngIf="!isLoading && errorMessage" class="error-container">
    <p class="text-danger">{{ errorMessage }}</p>
    <button class="btn-back-link" (click)="goBack()">← Back to list</button>
  </div>

  <main class="review-content" *ngIf="!isLoading && !errorMessage && draft">

    <!-- Registration Banner -->
    <div class="registration-banner success" *ngIf="draft.registrationStatus === 'REGISTERED'">
      <mat-icon>check_circle</mat-icon>
      <span>Complaint Registered: <strong>{{ draft.complaintNo }}</strong></span>
    </div>
    <div class="registration-banner failed" *ngIf="draft.registrationStatus === 'FAILED'">
      <mat-icon>error</mat-icon>
      <span>GRMIS registration failed for this complaint.</span>
    </div>

    <!-- Submit Feedback -->
    <div class="registration-banner success" *ngIf="submitSuccess">
      <mat-icon>check_circle</mat-icon>
      <span>{{ submitSuccess }}</span>
    </div>
    <div class="registration-banner failed" *ngIf="submitError">
      <mat-icon>error</mat-icon>
      <span>{{ submitError }}</span>
    </div>

    <!-- Missing Fields Alert -->
    <div class="missing-fields-banner" *ngIf="getMissingFieldsList().length > 0">
      <mat-icon>warning</mat-icon>
      <span>Missing fields: <strong>{{ getMissingFieldsList().join(', ') }}</strong></span>
    </div>

    <form [formGroup]="form">

      <!-- Citizen Details -->
      <section class="review-section" formGroupName="citizenDetails">
        <div class="section-header">
          <mat-icon>person_outline</mat-icon>
          <h2>Citizen Details</h2>
        </div>
        <div class="form-grid">

          <div class="form-field" [class.field-error]="f['citizenDetails'].get('name')?.invalid && f['citizenDetails'].get('name')?.touched">
            <label>Name <span class="required-mark">*</span></label>
            <input type="text" formControlName="name">
            <span class="error-hint" *ngIf="f['citizenDetails'].get('name')?.invalid && f['citizenDetails'].get('name')?.touched">Required</span>
          </div>

          <div class="form-field" [class.field-error]="f['citizenDetails'].get('gender')?.invalid && f['citizenDetails'].get('gender')?.touched">
            <label>Gender <span class="required-mark">*</span></label>
            <mat-select formControlName="gender" class="mat-select-field">
              <mat-option value="">Select</mat-option>
              <mat-option value="Male">Male</mat-option>
              <mat-option value="Female">Female</mat-option>
              <mat-option value="Transgender Person">Transgender Person</mat-option>
            </mat-select>
            <span class="error-hint" *ngIf="f['citizenDetails'].get('gender')?.invalid && f['citizenDetails'].get('gender')?.touched">Required</span>
          </div>

          <div class="form-field" [class.field-error]="f['citizenDetails'].get('cnic')?.invalid && f['citizenDetails'].get('cnic')?.touched">
            <label>CNIC <span class="required-mark">*</span></label>
            <input type="text" formControlName="cnic">
            <span class="error-hint" *ngIf="f['citizenDetails'].get('cnic')?.invalid && f['citizenDetails'].get('cnic')?.touched">Required</span>
          </div>

          <div class="form-field" [class.field-error]="f['citizenDetails'].get('phone')?.invalid && f['citizenDetails'].get('phone')?.touched">
            <label>Phone <span class="required-mark">*</span></label>
            <div class="phone-input">
              <span class="country-code">+92</span>
              <input type="text" formControlName="phone">
            </div>
            <span class="error-hint" *ngIf="f['citizenDetails'].get('phone')?.invalid && f['citizenDetails'].get('phone')?.touched">Required</span>
          </div>

          <div class="form-field">
            <label>Email</label>
            <input type="email" formControlName="email">
          </div>

          <div class="form-field">
            <label>Passport No</label>
            <input type="text" formControlName="passport">
          </div>

          <div class="form-field full-width">
            <label>Address</label>
            <textarea formControlName="address"></textarea>
          </div>
        </div>

        <div class="confidential-check">
          <mat-checkbox formControlName="keepConfidential">
            <strong>Keep Confidential</strong>
            <p>Check this box if the complainant wishes to hide their identity from the concerned department during the inquiry process.</p>
          </mat-checkbox>
        </div>
      </section>

      <!-- Complaint Details -->
      <section class="review-section" formGroupName="complaintDetails">
        <div class="section-header">
          <mat-icon>description</mat-icon>
          <h2>Complaint Details</h2>
        </div>
        <div class="form-grid">

          <div class="form-field" [class.field-error]="f['complaintDetails'].get('complaintCategoryId')?.invalid && f['complaintDetails'].get('complaintCategoryId')?.touched">
            <label>Complaint Category <span class="required-mark">*</span></label>
            <mat-select formControlName="complaintCategoryId" class="mat-select-field">
              <input class="w-100 border-0 p-1 select-search" placeholder="Search category" (keyup)="filterCategory($event)" (click)="$event.stopPropagation()" />
              <mat-option value="">Select</mat-option>
              <mat-option *ngFor="let c of allCategoryList" [value]="c.complaintCategoryId">{{ c.name }}</mat-option>
            </mat-select>
            <span class="error-hint" *ngIf="f['complaintDetails'].get('complaintCategoryId')?.invalid && f['complaintDetails'].get('complaintCategoryId')?.touched">Required</span>
          </div>

          <div class="form-field">
            <label>Sub Category</label>
            <mat-select formControlName="complaintSubcategoryId" class="mat-select-field">
              <input class="w-100 border-0 p-1 select-search" placeholder="Search sub category" (keyup)="filterSubCategory($event)" (click)="$event.stopPropagation()" />
              <mat-option value="">Select</mat-option>
              <mat-option *ngFor="let c of subCategoryList" [value]="c.complaintCategoryId">{{ c.name }}</mat-option>
            </mat-select>
          </div>

          <div class="form-field">
            <label>Days Required</label>
            <input type="text" formControlName="daysRequired">
          </div>

          <div class="form-field">
            <label>Legal Framework</label>
            <input type="text" formControlName="legalFramework">
          </div>

        </div>
      </section>

      <!-- Location Details -->
      <section class="review-section" formGroupName="locationDetails">
        <div class="section-header">
          <mat-icon>place</mat-icon>
          <h2>Location Details</h2>
        </div>
        <div class="form-grid">

          <div class="form-field">
            <label>Division</label>
            <mat-select formControlName="divisionId" class="mat-select-field">
              <input class="w-100 border-0 p-1 select-search" placeholder="Search division" (keyup)="filterDivisions($event)" (click)="$event.stopPropagation()" />
              <mat-option value="">Select</mat-option>
              <mat-option *ngFor="let d of allDivisionList" [value]="d.adminStrId">{{ d.name }}</mat-option>
            </mat-select>
          </div>

          <div class="form-field" [class.field-error]="f['locationDetails'].get('districtId')?.invalid && f['locationDetails'].get('districtId')?.touched">
            <label>District <span class="required-mark">*</span></label>
            <mat-select formControlName="districtId" class="mat-select-field">
              <input class="w-100 border-0 p-1 select-search" placeholder="Search district" (keyup)="filterDistricts($event)" (click)="$event.stopPropagation()" />
              <mat-option value="">Select</mat-option>
              <mat-option *ngFor="let d of districtList" [value]="d.adminStrId">{{ d.name }}</mat-option>
            </mat-select>
            <span class="error-hint" *ngIf="f['locationDetails'].get('districtId')?.invalid && f['locationDetails'].get('districtId')?.touched">Required</span>
          </div>

          <div class="form-field" [class.field-error]="f['locationDetails'].get('tehsilId')?.invalid && f['locationDetails'].get('tehsilId')?.touched">
            <label>Tehsil <span class="required-mark">*</span></label>
            <mat-select formControlName="tehsilId" class="mat-select-field">
              <input class="w-100 border-0 p-1 select-search" placeholder="Search tehsil" (keyup)="filterTehsil($event)" (click)="$event.stopPropagation()" />
              <mat-option value="">Select</mat-option>
              <mat-option *ngFor="let d of tehsilList" [value]="d.adminStrId">{{ d.name }}</mat-option>
            </mat-select>
            <span class="error-hint" *ngIf="f['locationDetails'].get('tehsilId')?.invalid && f['locationDetails'].get('tehsilId')?.touched">Required</span>
          </div>

          <div class="form-field">
            <label>Mouza / Village</label>
            <mat-select formControlName="mouzaId" class="mat-select-field">
              <input class="w-100 border-0 p-1 select-search" placeholder="Search mouza" (keyup)="filterMouza($event)" (click)="$event.stopPropagation()" />
              <mat-option value="">Select</mat-option>
              <mat-option *ngFor="let d of mouzaList" [value]="d.adminStrId">{{ d.name }}</mat-option>
            </mat-select>
          </div>

          <div class="form-field full-width">
            <label>Complaint Address</label>
            <textarea formControlName="complaintAddress"></textarea>
          </div>

        </div>
      </section>

      <!-- Attachments -->
      <section class="review-section">
        <div class="section-header">
          <mat-icon>attach_file</mat-icon>
          <h2>{{ attachments.length }} ATTACHMENT(S)</h2>
        </div>
        <div class="attachments-grid" *ngIf="attachments.length > 0">
          <div class="attachment-item" *ngFor="let file of attachments">
            <div class="file-icon"><mat-icon>{{ getAttachmentIcon(file.mimeType) }}</mat-icon></div>
            <div class="file-info">
              <span class="file-name">{{ file.fileName }}</span>
              <span class="file-size">{{ formatAttachmentSize(file.fileSize) }}</span>
            </div>
            <button class="btn-download" title="Download" (click)="downloadAttachment(file)"><mat-icon>download</mat-icon></button>
          </div>
        </div>
        <div class="no-attachments" *ngIf="attachments.length === 0">
          <p>No attachments found for this complaint.</p>
        </div>
      </section>

      <!-- Description -->
      <section class="review-section">
        <div class="section-header">
          <mat-icon>subject</mat-icon>
          <h2>Description</h2>
        </div>
        <div class="editor-container">
          <div class="ngx-editor-wrapper">
            <ngx-editor-menu [editor]="editor" [toolbar]="toolbar"></ngx-editor-menu>
            <ngx-editor [editor]="editor" formControlName="description"></ngx-editor>
          </div>
        </div>
      </section>

    </form>
  </main>
</div>

<!-- ─── Confirm Reject Modal ─── -->
<div *ngIf="showRejectModal" class="reject-overlay" (click)="showRejectModal = false">
  <div class="reject-modal" (click)="$event.stopPropagation()">
    <h3 class="reject-title">Reject this email?</h3>
    <p class="reject-msg">It will be moved to the black list and removed from the pending queue.</p>

    <label class="reject-label" for="rejectReason">Reason</label>
    <select id="rejectReason" class="reject-select" [(ngModel)]="rejectReason" [ngModelOptions]="{standalone: true}">
      <option value="IRRELEVANT">Irrelevant</option>
      <option value="SPAM">Spam</option>
      <option value="PROMOTIONAL">Promotional</option>
    </select>

    <div class="reject-actions">
      <button class="btn-cancel" (click)="showRejectModal = false" [disabled]="isRejecting">Cancel</button>
      <button class="btn-reject" (click)="confirmReject()" [disabled]="isRejecting">
        {{ isRejecting ? 'Rejecting...' : 'Reject' }}
      </button>
    </div>
  </div>
</div>
